import { getSupabaseClient } from '../../lib/supabase';

export type LevelUnitProgress = {
  unitCode: string;
  unitName: string;
  completedCheckpoints: number;
  totalCheckpoints: number;
  progressPercent: number;
};

export type NextCheckpointSummary = {
  code: string;
  title: string;
  unitCode: string;
  unitName: string;
  status: string;
};

export type LevelProgressSummary = {
  schemaVersion: 2;
  cycleId: string;
  subjectCode: string;
  levelId: string;
  levelCode: string;
  levelName: string;
  progressPercent: number;
  checkpointCompleted: number;
  checkpointTarget: number;
  checkpointInProgress: number;
  curriculumVersion: string;
  progressSource: string;
  readinessStatus: string;
  nextCheckpoint: NextCheckpointSummary | null;
  units: LevelUnitProgress[];
  legacyEvidenceCompleted: number;
  legacyEvidenceTarget: number;
  startedAt: string;
  status: string;
};

function numberValue(value: unknown, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function objectValue(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? value as Record<string, unknown> : null;
}

function parseUnit(value: unknown): LevelUnitProgress | null {
  const row = objectValue(value);
  if (!row) return null;
  return {
    unitCode: String(row.unitCode ?? ''),
    unitName: String(row.unitName ?? row.unitCode ?? 'Bagian'),
    completedCheckpoints: Math.max(0, Math.round(numberValue(row.completedCheckpoints))),
    totalCheckpoints: Math.max(0, Math.round(numberValue(row.totalCheckpoints))),
    progressPercent: Math.max(0, Math.min(100, numberValue(row.progressPercent))),
  };
}

function parseNextCheckpoint(value: unknown): NextCheckpointSummary | null {
  const row = objectValue(value);
  if (!row) return null;
  return {
    code: String(row.code ?? ''),
    title: String(row.title ?? 'Tahap berikutnya'),
    unitCode: String(row.unitCode ?? ''),
    unitName: String(row.unitName ?? ''),
    status: String(row.status ?? 'not_started'),
  };
}

function parseSummary(value: unknown): LevelProgressSummary {
  const row = objectValue(value);
  if (!row) throw new Error('Level progress response tidak dikenali.');
  const units = Array.isArray(row.units) ? row.units.map(parseUnit).filter((unit): unit is LevelUnitProgress => Boolean(unit)) : [];
  return {
    schemaVersion: 2,
    cycleId: String(row.cycleId ?? ''),
    subjectCode: String(row.subjectCode ?? ''),
    levelId: String(row.levelId ?? ''),
    levelCode: String(row.levelCode ?? ''),
    levelName: String(row.levelName ?? row.levelCode ?? 'Level'),
    progressPercent: Math.max(0, Math.min(100, numberValue(row.progressPercent))),
    checkpointCompleted: Math.max(0, Math.round(numberValue(row.checkpointCompleted))),
    checkpointTarget: Math.max(0, Math.round(numberValue(row.checkpointTarget))),
    checkpointInProgress: Math.max(0, Math.round(numberValue(row.checkpointInProgress))),
    curriculumVersion: String(row.curriculumVersion ?? ''),
    progressSource: String(row.progressSource ?? ''),
    readinessStatus: String(row.readinessStatus ?? 'not_ready'),
    nextCheckpoint: parseNextCheckpoint(row.nextCheckpoint),
    units,
    legacyEvidenceCompleted: Math.max(0, Math.round(numberValue(row.legacyEvidenceCompleted))),
    legacyEvidenceTarget: Math.max(0, Math.round(numberValue(row.legacyEvidenceTarget))),
    startedAt: String(row.startedAt ?? ''),
    status: String(row.status ?? 'active'),
  };
}

export async function ensureCurrentMathLevelProgress(): Promise<LevelProgressSummary> {
  const client = getSupabaseClient();
  if (!client) throw new Error('Supabase belum dikonfigurasi.');
  const { data, error } = await client.rpc('ensure_student_level_cycle_v2', {
    p_subject_code: 'MTH',
    p_level_code: 'C',
  });
  if (error) throw error;
  return parseSummary(data);
}
